<template>
    <transition name="fade">
        <div class="embed-modal relative">
            <div @click="closeModal" class="modal-backdrop absolute w-full h-full"></div>
            <div class="modal-content relative">
                <InnerLoader v-if="isLoading" />
                <div class="modal-header mb-2">
                    <div class="flex justify-between">
                        <h3 class="font-size-20 shadow-text-none p-0 m-0">
                            {{ translate('embed_modal_header') }}
                        </h3>
                        <div class="leading-none">
                            <i @click="closeModal" class="fas fa-times text-xl shadow-text-none cursor-pointer"></i>
                        </div>
                    </div>
                </div>

                <div class="modal-body">
                    <div class="flex flex-wrap">
                        <div class="mb-2 w-1/2">
                            <div class="pr-1">
                                <span class="font-size-18 shadow-text-none mb-2">{{ $t('embed_width') }}</span>
                                <XInput
                                    @change="changeEmbedSize('width', $event)"
                                    :value="width"
                                    class="size-input"
                                    type="text"
                                ></XInput>
                            </div>
                        </div>
                        <div class="mb-2 w-1/2">
                            <div class="pl-1">
                                <span class="font-size-18 shadow-text-none mb-2">{{ $t('embed_height') }}</span>
                                <XInput
                                    @change="changeEmbedSize('height', $event)"
                                    :value="height"
                                    type="text"
                                    class="size-input"
                                ></XInput>
                            </div>
                        </div>
                    </div>
                    <XTextarea :value="genEmbedURL()" id="embed-input" class="mt-3 p-4 w-full size-input" readonly />

                    <XButton
                        @click="onCopy"
                        class="w-full mt-5 system-button-color system-button-text-color shadow-text-none"
                    >
                        <i class="fas fa-paste"></i> {{ translate('embed_modal_button_copy_url') }}
                    </XButton>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import appConfig from '@/app.config'
import helperMixins from '@/mixins/helper'
import InnerLoader from '@/components/InnerLoader'
import XTextarea from '@/components/v2/XTextarea'
import XButton from '@/components/v2/XButton'
import XInput from './v2/XInput.vue'

export default {
    components: {
        InnerLoader,
        XTextarea,
        XButton,
        XInput
    },

    mixins: [helperMixins],

    props: {
        showCloseButton: {
            type: Boolean,
            default: true
        },

        quiz: {
            type: Object
        },

        embed: {
            type: Object
        }
    },

    data() {
        return {
            isLoading: false,
            width: 960,
            height: 540,
            embedSize: {}
        }
    },

    mounted() {
        document.querySelector('#embed-input').addEventListener('click', () => {
            this.selectText('#embed-input')
        })

        if (localStorage.getItem('embed-size')) {
            this.embedSize = JSON.parse(localStorage.getItem('embed-size'))
            this.width = this.embedSize.width
            this.height = this.embedSize.height
        }
    },

    methods: {
        closeModal() {
            this.$emit('close')
        },

        genEmbedURL() {
            return `<iframe width="${this.width}" height="${this.height}" src="${appConfig.baseUrl}embed/${this.embed.token_user}" title="${this.quiz.title}" allow="clipboard-read; clipboard-write" frameborder="0" allowfullscreen></iframe>`
        },

        selectText(containerid) {
            const id = document.querySelector(containerid)
            id.select()
        },

        onCopy() {
            let copyText = document.querySelector('#embed-input')
            copyText.select()
            copyText.setSelectionRange(0, 99999)
            navigator.clipboard.writeText(copyText.value)
            this.$emit('copied', copyText.value)
        },

        changeEmbedSize(type, e) {
            this[type] = e.target.value.replace(/[^0-9|%]+/g, '')
            this.embedSize = {
                width: this.width,
                height: this.height
            }
            localStorage.setItem('embed-size', JSON.stringify(this.embedSize))
        }
    }
}
</script>

<style lang="scss" scoped>
@import '@/scss/variable.scss';
@import '@/scss/mixin.scss';

.embed-modal {
    font-family: 'Open Sans', sans-serif !important;
    background-color: rgba(0, 0, 0, 0.2);
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: start;
    font-size: 1.2em;
    z-index: 9999;

    .modal-backdrop {
        z-index: 1;
    }

    .modal-content {
        background-color: #fff;
        margin-top: 4rem;
        padding: 1rem 1.5rem;
        width: 500px !important;
        overflow: hidden;
        border-radius: 3px;
        box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.6);
        z-index: 2;

        @include screen-max-md {
            width: 90% !important;
            max-height: 95vh;
            margin: 1rem 0.5rem;
        }

        .input {
            font-weight: bold;
            padding: 0.8rem;
        }

        .modal-body {
            margin: 0 0 1rem 0;

            @media screen and (max-width: 500px) {
                margin: 0 0 1rem 0;
                max-height: 80vh;
                overflow-y: auto;
            }

            .url-input {
                border: 1px solid $input-border-color;
            }

            #report-select {
                padding: 0.8rem;
                width: 100%;
                background-color: transparent;
                border: 1px solid $input-border-color;
                border-radius: 4px;
                font-size: 18px;
                font-weight: bold;

                option {
                    font-size: 16px;
                }

                @include screen-max-md {
                    font-size: 16px;
                }
            }

            .size-input {
                border: 1px solid $input-border-color;
                font-weight: 500;
                font-size: 16px;
            }

            input::-webkit-outer-spin-button,
            input::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }

            /* Firefox */
            input[type='number'] {
                -moz-appearance: textfield;
            }

            #embed-input {
                border: 1px solid $input-border-color;
                text-align: left;
                font-size: 16px;
            }

            textarea {
                height: 150px;
                font-family: monospace;
            }
        }
    }
}
</style>
